// src/pages/UserManagement.js - Admin Panel for Board Member Management
import React, { useState, useEffect } from 'react';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import { PAGES } from '../config/pages';
import '../css/UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [securityStatus, setSecurityStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isGeneratingPassword, setIsGeneratingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Huismeester-account aanmaken (voorgevuld met het gewenste account)
  const [huismeesterEmail, setHuismeesterEmail] = useState('h.gool@chello.nl');
  const [huismeesterPassword, setHuismeesterPassword] = useState('TopKlus2026');
  const [isAddingHuismeester, setIsAddingHuismeester] = useState(false);

  // Pagina-toegang per gebruiker (lokale bewerkingen): { [userId]: [pageKeys] }
  const [accessEdits, setAccessEdits] = useState({});
  const [savingAccessId, setSavingAccessId] = useState(null);

  // Fetch users and security status
  useEffect(() => {
    fetchUsers();
    fetchSecurityStatus();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data);

      // Seed lokale pagina-toegang-bewerkingen vanuit de opgehaalde gebruikers
      const edits = {};
      response.data.forEach((u) => {
        edits[u._id] = Array.isArray(u.allowedPages) ? u.allowedPages : [];
      });
      setAccessEdits(edits);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Kon gebruikers niet ophalen');
    } finally {
      setLoading(false);
    }
  };

  // Pagina aan/uit zetten voor een bestuurslid (lokaal, nog niet opgeslagen)
  const togglePageAccess = (userId, pageKey) => {
    setAccessEdits((prev) => {
      const current = prev[userId] || [];
      const next = current.includes(pageKey)
        ? current.filter((k) => k !== pageKey)
        : [...current, pageKey];
      return { ...prev, [userId]: next };
    });
  };

  // Pagina-toegang opslaan voor een bestuurslid
  const savePageAccess = async (userId, userEmail) => {
    setSavingAccessId(userId);
    setError('');
    setSuccess('');
    try {
      const allowedPages = accessEdits[userId] || [];
      const response = await api.put(`/api/admin/users/${userId}/access`, { allowedPages });
      if (response.data.success) {
        setSuccess(`Pagina-toegang opgeslagen voor ${userEmail}`);
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Kon pagina-toegang niet opslaan');
    } finally {
      setSavingAccessId(null);
    }
  };

  // Huismeester (her)vergrendelen op de Huismeester-app via het bestaande /access-endpoint
  const lockHuismeester = async (userId, userEmail) => {
    setSavingAccessId(userId);
    setError('');
    setSuccess('');
    try {
      const response = await api.put(`/api/admin/users/${userId}/access`, { allowedPages: ['schilder'] });
      if (response.data.success) {
        setSuccess(`${userEmail} vergrendeld op de Huismeester-app`);
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Kon vergrendeling niet opslaan');
    } finally {
      setSavingAccessId(null);
    }
  };

  const fetchSecurityStatus = async () => {
    try {
      const response = await api.get('/api/auth/security-status');
      setSecurityStatus(response.data);
    } catch (err) {
      console.error('Error fetching security status:', err);
    }
  };

  // Generate secure password
  const generateSecurePassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*';
    
    let password = '';
    
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest with random characters
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  };

  // Reset user password
  const resetUserPassword = async (userId, userEmail) => {
    if (!window.confirm(`Wachtwoord resetten voor ${userEmail}?`)) {
      return;
    }

    setIsGeneratingPassword(true);
    setError('');
    setSuccess('');

    try {
      const newPassword = generateSecurePassword();
      
      const response = await api.post('/api/admin/reset-password', {
        userId,
        newPassword
      });

      if (response.data.success) {
        setNewPassword(newPassword);
        setShowPassword(true);
        setSuccess(`Wachtwoord succesvol gereset voor ${userEmail}`);
        
        // Refresh users list
        fetchUsers();
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.error || 'Kon wachtwoord niet resetten');
    } finally {
      setIsGeneratingPassword(false);
    }
  };

  // Copy password to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Wachtwoord gekopieerd naar klembord!');
    });
  };

  // Clear lockout for user
  const clearUserLockout = async (email) => {
    try {
      await api.post('/api/admin/clear-lockout', { email });
      setSuccess(`Vergrendeling opgeheven voor ${email}`);
      fetchSecurityStatus();
    } catch (err) {
      setError('Kon vergrendeling niet opheffen');
    }
  };

  // Add new user
  const addNewUser = async () => {
    const email = prompt('Voer het e-mailadres van het nieuwe bestuurslid in:');
    if (!email || !email.includes('@')) {
      alert('Ongeldig e-mailadres');
      return;
    }

    try {
      const tempPassword = generateSecurePassword();
      
      const response = await api.post('/api/admin/add-user', {
        email: email.toLowerCase().trim(),
        password: tempPassword,
        role: 'board_member'
      });

      if (response.data.success) {
        setNewPassword(tempPassword);
        setShowPassword(true);
        setSuccess(`Nieuwe gebruiker aangemaakt: ${email}`);
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Kon gebruiker niet aanmaken');
    }
  };

  // Add huismeester account (schilder-/huismeesterportaal)
  const addHuismeester = async () => {
    const email = huismeesterEmail.toLowerCase().trim();
    const password = huismeesterPassword;

    if (!email || !email.includes('@')) {
      setError('Ongeldig e-mailadres voor huismeester');
      return;
    }
    if (!password || password.length < 8) {
      setError('Wachtwoord huismeester moet minimaal 8 tekens zijn');
      return;
    }

    setIsAddingHuismeester(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/admin/add-user', {
        email,
        password,
        role: 'huismeester'
      });

      if (response.data.success) {
        setSuccess(`Huismeester-account aangemaakt: ${email}`);
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Kon huismeester-account niet aanmaken');
    } finally {
      setIsAddingHuismeester(false);
    }
  };

  // Remove user
  const removeUser = async (userId, userEmail) => {
    if (!window.confirm(`Gebruiker ${userEmail} definitief verwijderen?`)) {
      return;
    }

    try {
      await api.delete(`/api/admin/users/${userId}`);
      setSuccess(`Gebruiker ${userEmail} verwijderd`);
      fetchUsers();
    } catch (err) {
      setError('Kon gebruiker niet verwijderen');
    }
  };

  if (loading) {
    return (
      <div className="beheer-wrapper">
        <BeheerdersportaalSidebar>
          <main className="dashboard">
            <div className="loading">Bezig met laden...</div>
          </main>
        </BeheerdersportaalSidebar>
      </div>
    );
  }

  return (
    <div className="beheer-wrapper">
      <BeheerdersportaalSidebar>
        <main className="dashboard user-management">
          <h1>👥 Gebruikersbeheer</h1>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* New Password Display */}
          {showPassword && newPassword && (
            <div className="new-password-container">
              <h3>🔑 Nieuw Wachtwoord Gegenereerd</h3>
              <div className="password-display">
                <code>{newPassword}</code>
                <button 
                  onClick={() => copyToClipboard(newPassword)}
                  className="copy-button"
                >
                  📋 Kopiëren
                </button>
              </div>
              <p className="password-notice">
                <strong>Belangrijk:</strong> Deel dit wachtwoord veilig met het bestuurslid via een beveiligd kanaal. 
                Dit wachtwoord wordt niet opnieuw getoond.
              </p>
              <button 
                onClick={() => setShowPassword(false)}
                className="close-password-button"
              >
                Sluiten
              </button>
            </div>
          )}

          {/* Security Status */}
          {securityStatus && (
            <div className="security-status-container">
              <h2>🔐 Beveiligingsstatus</h2>
              <div className="security-stats">
                <div className="stat-item">
                  <span className="stat-number">{securityStatus.totalActiveAttempts}</span>
                  <span className="stat-label">Actieve Mislukte Pogingen</span>
                </div>
              </div>
              
              {securityStatus.currentAttempts.length > 0 && (
                <div className="failed-attempts">
                  <h3>Recente Mislukte Pogingen</h3>
                  <div className="attempts-list">
                    {securityStatus.currentAttempts.map((attempt, index) => (
                      <div 
                        key={index} 
                        className={`attempt-item ${attempt.isLocked ? 'locked' : ''}`}
                      >
                        <div className="attempt-info">
                          <strong>{attempt.email}</strong>
                          <span className="attempt-ip">IP: {attempt.ip}</span>
                          <span className="attempt-count">{attempt.attempts} pogingen</span>
                          {attempt.isLocked && <span className="locked-badge">🔒 VERGRENDELD</span>}
                        </div>
                        {attempt.isLocked && (
                          <button 
                            onClick={() => clearUserLockout(attempt.email)}
                            className="unlock-button"
                          >
                            Ontgrendelen
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Management */}
          <div className="users-container">
            <div className="users-header">
              <h2>👤 Bestuursledenaccounts</h2>
              <button onClick={addNewUser} className="add-user-button">
                ➕ Nieuw Bestuurslid
              </button>
            </div>

            <div className="users-list">
              {users.filter((u) => u.role !== 'huismeester').map((user) => {
                const edited = accessEdits[user._id] || [];
                const fullAccess = edited.length === 0;
                const dirty =
                  JSON.stringify([...edited].sort()) !==
                  JSON.stringify([...(user.allowedPages || [])].sort());
                return (
                <div key={user._id} className="user-card">
                  <div className="user-info">
                    <div className="user-email">{user.email}</div>
                    <div className="user-details">
                      <span>Aangemaakt: {new Date(user.createdAt).toLocaleDateString('nl-NL')}</span>
                      <span>Laatste login: {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('nl-NL') : 'Nooit'}</span>
                    </div>
                  </div>

                  {/* Pagina-toegang per bestuurslid */}
                  <div className="user-access">
                    <div className="user-access-header">
                      <strong>🔓 Pagina-toegang</strong>
                      <span className="access-hint">
                        {fullAccess ? 'Volledige toegang (geen vinkjes = alles)' : `${edited.length} pagina('s) geselecteerd`}
                      </span>
                    </div>
                    <div className="access-grid">
                      {PAGES.map((page) => (
                        <label key={page.key} className="access-option">
                          <input
                            type="checkbox"
                            checked={edited.includes(page.key)}
                            onChange={() => togglePageAccess(user._id, page.key)}
                          />
                          <span>{page.label}</span>
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={() => savePageAccess(user._id, user.email)}
                      className="save-access-button"
                      disabled={!dirty || savingAccessId === user._id}
                    >
                      {savingAccessId === user._id ? '⏳ Opslaan...' : '💾 Toegang opslaan'}
                    </button>
                  </div>

                  <div className="user-actions">
                    <button
                      onClick={() => resetUserPassword(user._id, user.email)}
                      className="reset-password-button"
                      disabled={isGeneratingPassword}
                    >
                      {isGeneratingPassword ? '⏳ Bezig...' : '🔄 Reset Wachtwoord'}
                    </button>
                    <button
                      onClick={() => removeUser(user._id, user.email)}
                      className="remove-user-button"
                    >
                      🗑️ Verwijderen
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Huismeester accounts */}
          <div className="users-container huismeester-container">
            <div className="users-header">
              <h2>🛠️ Huismeester account(s)</h2>
            </div>

            <p className="huismeester-intro">
              Een huismeester-account ziet <strong>alleen de Huismeester-app</strong> en heeft geen
              toegang tot de overige beheerpagina's.
            </p>

            {/* Aanmaken */}
            <div className="huismeester-create">
              <div className="huismeester-fields">
                <label>
                  E-mailadres
                  <input
                    type="email"
                    value={huismeesterEmail}
                    onChange={(e) => setHuismeesterEmail(e.target.value)}
                    placeholder="h.gool@chello.nl"
                  />
                </label>
                <label>
                  Wachtwoord
                  <input
                    type="text"
                    value={huismeesterPassword}
                    onChange={(e) => setHuismeesterPassword(e.target.value)}
                    placeholder="Minimaal 8 tekens"
                  />
                </label>
              </div>
              <button
                onClick={addHuismeester}
                className="add-user-button"
                disabled={isAddingHuismeester}
              >
                {isAddingHuismeester ? '⏳ Bezig...' : '➕ Maak er een aan'}
              </button>
            </div>

            {/* Bestaande huismeester-accounts */}
            <div className="users-list">
              {users.filter((u) => u.role === 'huismeester').length === 0 && (
                <p className="huismeester-empty">Nog geen huismeester-account aangemaakt.</p>
              )}
              {users.filter((u) => u.role === 'huismeester').map((user) => {
                const pages = Array.isArray(user.allowedPages) ? user.allowedPages : [];
                const vergrendeld = pages.length === 1 && pages[0] === 'schilder';
                return (
                <div key={user._id} className="user-card">
                  <div className="user-info">
                    <div className="user-email">{user.email}</div>
                    <div className="user-details">
                      <span>🛠️ Huismeester · alleen Huismeester-app</span>
                      <span>Aangemaakt: {new Date(user.createdAt).toLocaleDateString('nl-NL')}</span>
                      <span>Laatste login: {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('nl-NL') : 'Nooit'}</span>
                    </div>
                  </div>

                  {/* Vergrendel-status: leeg/anders = volledige toegang, dus waarschuwen + herstelknop */}
                  <div className="user-access">
                    {vergrendeld ? (
                      <span className="access-hint">🔒 Vergrendeld op de Huismeester-app</span>
                    ) : (
                      <>
                        <span className="access-hint" style={{ color: '#c0392b' }}>
                          ⚠️ Niet vergrendeld — dit account heeft nu volledige toegang
                        </span>
                        <button
                          onClick={() => lockHuismeester(user._id, user.email)}
                          className="save-access-button"
                          disabled={savingAccessId === user._id}
                        >
                          {savingAccessId === user._id ? '⏳ Opslaan...' : '🔒 Vergrendel op Huismeester-app'}
                        </button>
                      </>
                    )}
                  </div>

                  <div className="user-actions">
                    <button
                      onClick={() => resetUserPassword(user._id, user.email)}
                      className="reset-password-button"
                      disabled={isGeneratingPassword}
                    >
                      {isGeneratingPassword ? '⏳ Bezig...' : '🔄 Reset Wachtwoord'}
                    </button>
                    <button
                      onClick={() => removeUser(user._id, user.email)}
                      className="remove-user-button"
                    >
                      🗑️ Verwijderen
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Password Requirements Info */}
          <div className="password-requirements">
            <h3>📋 Wachtwoord Vereisten</h3>
            <ul>
              <li>Minimaal 8 karakters</li>
              <li>Minimaal 1 hoofdletter (A-Z)</li>
              <li>Minimaal 1 kleine letter (a-z)</li>
              <li>Minimaal 1 cijfer (0-9)</li>
              <li>Minimaal 1 speciaal teken (!@#$%^&*)</li>
            </ul>
            <p>Automatisch gegenereerde wachtwoorden voldoen altijd aan deze eisen.</p>
          </div>
        </main>
      </BeheerdersportaalSidebar>
    </div>
  );
};

export default UserManagement;